import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import '@/App.css';
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import Dashboard from '@/pages/Dashboard';
import EditorPage from '@/pages/EditorPage';
import AdminDashboard from '@/pages/AdminDashboard';
import PublicCard from '@/pages/PublicCard';
import ProfilePage from '@/pages/ProfilePage';
import PaymentCallback from '@/pages/PaymentCallback';
import TemplateSelectionPage from '@/pages/TemplateSelectionPage';
import PreviewKadPage from '@/pages/PreviewKadPage';
import AuthCallback from '@/components/AuthCallback';
import ProtectedRoute from '@/components/ProtectedRoute';
import MaintenancePage from '@/pages/MaintenancePage';
import { Toaster } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AppRouter() {
  const location = useLocation();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [siteChecked, setSiteChecked] = useState(false);

  useEffect(() => {
    const checkSiteStatus = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/site/status`);
        setMaintenanceMode(res.data.maintenance_mode || false);
        setMaintenanceMessage(res.data.maintenance_message || '');
      } catch (e) {
        // If unreachable, allow through
        setMaintenanceMode(false);
      } finally {
        setSiteChecked(true);
      }
    };
    checkSiteStatus();
  }, []);

  if (!siteChecked) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#3D348B]"></div>
      </div>
    );
  }

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Check if current path is admin - admins bypass maintenance
  const isAdminPath = location.pathname.startsWith('/admin');
  const isAuthPath = location.pathname === '/login' || location.pathname === '/register';
  const token = localStorage.getItem('session_token');

  // If maintenance mode is on, show maintenance page for non-admin paths
  // But allow /admin, /login routes through
  if (maintenanceMode && !isAdminPath && !isAuthPath) {
    // Allow admins with existing sessions through (they can navigate to /admin)
    return <MaintenancePage message={maintenanceMessage} />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/preview-kad" element={<PreviewKadPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates"
        element={
          <ProtectedRoute>
            <TemplateSelectionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/callback"
        element={<PaymentCallback />}
      />
      <Route
        path="/editor/:cardId"
        element={
          <ProtectedRoute>
            <EditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/:slug" element={<PublicCard />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;